import * as React from "react";
import Modal from "../../misc/components/modal";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { Form, useFormApi, useFormState } from "informedv4";
import { Required } from "../../forms/validation/Required";
import { MultiSelect } from "../../forms/informedv4/MultiSelectV4";
import { Dropdown } from "../../forms/informedv4/DropdownV4";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { AccessRequest, TimeZone } from "webportalclient";

export interface RequestAcceptanceModalProps extends RequestAcceptanceFormProps {
    show: boolean;
    error: string | undefined;
}

export interface RequestAcceptanceFormProps {
    request: AccessRequest;
    roles: any[];
    roleOptions: any[];
    decisionButtonsDisabled: boolean;
    onAccept: (userId: string, rolesIds: string[], sitesIds: string[], timeZone: string, sp6Group: string) => void;
    onClose: () => void;
}

interface RequestAcceptanceFormState {
    roles: any[];
    sites: any[];
    timeZone: TimeZone;
}

export const RequestAcceptanceForm = (props: RequestAcceptanceFormProps) => {
    const [siteOptions, setSiteOptions] = React.useState([]);
    const client = useWebPortalApiClient();
    const formApi = useFormApi();
    const formState = useFormState();
    const [timeZoneOptions, setTimeZoneOptions] = React.useState([]);
    const [spirotracUserGroupOptions, setSpirotracUserGroupOptions] = React.useState([]);

    React.useEffect(() => {
        const getTimeZones = async () => {
            const timeZones = await client.client.getTimeZones("id,name", undefined, "sortOrder");
            setTimeZoneOptions(timeZones.value);
            formApi.setValue("timeZone", timeZones.value.find(t => t.id === props.request.timeZoneId));
        }

        const getSpVIGroups = async () => {
            const spirotracUserGroups = await client.client.getSpirotracUserGroups("id,name");
            setSpirotracUserGroupOptions(spirotracUserGroups.value);
        }

        getTimeZones();
        getSpVIGroups();
    }, []);

    const updateSites = async () => {
        const selectedRoles: any[] = formApi.getValue("roles") as any[];
        const rolesSelected = selectedRoles && selectedRoles.length > 0;
        if (!rolesSelected)
            return;

        const apiRolesRelatedToStudies = getApiRoles(selectedRoles).filter(r => r.studyId);
        if (apiRolesRelatedToStudies.length === 0) {
            setSiteOptions(undefined);
            formApi.setValue("sites", undefined);
            return;
        }

        const filter = rolesSelected ? `studyId in (${apiRolesRelatedToStudies.map(r => r.studyId).toString()})` : undefined;
        const select = rolesSelected ? "id,number,study" : "id,number";
        const sites = await client.client.getSites(select, filter, "number", "study($select=name)");
        const updatedOptions = sites.value.map(v => ({ label: `(${v.study.name}) ${v.number}`, value: v.id }));
        setSiteOptions(updatedOptions);
        
        const selectedSites: any[] = formApi.getValue("sites") as any[];
        if (!selectedSites)
            return;
        const updatedSites = selectedSites.filter(s => updatedOptions.map(o => o.value).includes(s.value));
        formApi.setValue("sites", updatedSites);
    }

    const getApiRoles = (selectedRoles: any[]) => props.roles.filter(r => selectedRoles.some(v => v.value === r.id));

    const isPermissionRequired = (selectedRoles: any[], permission: PermissionEnum) => {
        return getApiRoles(selectedRoles).filter(r => r.name !== "Administrator").some(r => r.permissionsIds.includes(permission));
    }

    const validateSites = (value: any, values: any) => {
        if ((!value || value.length === 0) && values.roles && values.roles.length > 0) {
            const siteRequired = isPermissionRequired(values.roles, PermissionEnum.RequiresSite);
            if (siteRequired)
                return "A site selection is required for this user's role selections";
        }
    }

    const validateForm = () => {
        formApi.validate();
        const state = formApi.getFormState();
        return state.errors && Object.keys(state.errors).length === 0 && Object.getPrototypeOf(state.errors) === Object.prototype;
    }

    const onSubmit = () => {
        const valid = validateForm();
        if (!valid)
            return;

        const roleIds = !formState.values.roles || (formState.values.roles as any[]).length === 0 ? [] : (formState.values.roles as any[]).map(r => r.value);
        const siteIds = !formState.values.sites || (formState.values.sites as any[]).length === 0 ? [] : (formState.values.sites as any[]).map(r => r.value);
        props.onAccept(props.request.id, siteIds, roleIds, (formState.values.timeZone as TimeZone)?.id, formState.values.spirotracUserGroup as string);
    }

    return (<>
        <div className="modal-body">
            <div className="form-group">
                <label>Roles</label>
                <MultiSelect
                    name="roles"
                    id="new-user-roles"
                    field="roles"
                    labelKey="label"
                    valueKey="value"
                    multi
                    onChange={updateSites}
                    options={props.roleOptions}
                    validate={Required}
                    disabled={props.decisionButtonsDisabled}
                />
            </div>
            <div className="form-group">
                <label>Sites</label>
                <MultiSelect
                    name="sites"
                    id="new-user-sites"
                    field="sites"
                    labelKey="label"
                    valueKey="value"
                    multi
                    options={siteOptions}
                    validate={validateSites}
                    disabled={props.decisionButtonsDisabled}
                />
            </div>
            <div className="form-group">
                <label htmlFor="timeZone">Preferred Time Zone</label>
                <MultiSelect
                    name="timeZone"
                    id="timeZone"
                    field="timeZone"
                    labelKey="name"
                    valueKey="id"
                    options={timeZoneOptions}
                    validate={Required}
                    disabled={props.request?.timeZoneId !== undefined && props.request?.timeZoneId !== null}
                />
            </div>
            <div className="form-group">
                <label htmlFor="spirotracUserGroup">Spirotrac VI User Group</label>
                <Dropdown name="spirotracUserGroup" id="spirotracUserGroup" field="spirotracUserGroup">
                    {spirotracUserGroupOptions.map(cr => (<option key={`${cr.id}`} value={cr.id}>{cr.name}</option>))}
                </Dropdown>
            </div>
        </div>
        <div className="modal-footer">
            <button className="btn btn-primary" onClick={onSubmit} disabled={props.decisionButtonsDisabled}>
                Accept
            </button>
            <button className="btn btn-outline btn-primary" onClick={() => props.onClose()}>
                Close
            </button>
        </div>
    </>);
}

export const RequestAcceptanceModal = (props: RequestAcceptanceModalProps) => {
    return (
        <Modal show={props.show} closeModalHandler={() => props.onClose()} header="Assign Roles to User">
            <Alert show={props.error !== undefined} category={AlertCategoryEnum.Danger}>{props.error}</Alert>
            <Form validateOn="submit" noValidate>
                <RequestAcceptanceForm
                    request={props.request}
                    roles={props.roles}
                    roleOptions={props.roleOptions}
                    decisionButtonsDisabled={props.decisionButtonsDisabled}
                    onAccept={props.onAccept}
                    onClose={props.onClose}
                />
            </Form>
        </Modal>
    );
}
